import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useBranding, useUpsertBranding } from "@/hooks/useBranding";
import { COLOR_PALETTES, type ColorPalette } from "@/lib/branding-keys";
import { hslTripletToRgb, contrastRatio, wcagLevel } from "@/lib/contrast";

type ColorKey = keyof ColorPalette["values"];

const COLOR_KEYS: Array<{ key: ColorKey; label: string; help?: string }> = [
  { key: "primary",     label: "Primary",     help: "Main brand color — buttons, links, accents." },
  { key: "accent",      label: "Accent",      help: "Highlights and secondary CTAs." },
  { key: "background",  label: "Background",  help: "Page surface (usually white or near-white)." },
  { key: "foreground",  label: "Foreground",  help: "Body text color on the background." },
  { key: "muted",       label: "Muted",       help: "Subtle surfaces — card backgrounds, dividers." },
  { key: "destructive", label: "Destructive", help: "Error states and delete actions." },
];

function Swatch({ triplet, className = "" }: { triplet: string; className?: string }) {
  const rgb = hslTripletToRgb(triplet);
  const bg = rgb ? `rgb(${rgb.r} ${rgb.g} ${rgb.b})` : "transparent";
  return <div className={`rounded-md border border-border/60 ${className}`} style={{ backgroundColor: bg }} />;
}

function ContrastBadge({ fg, bg, label }: { fg: string; bg: string; label: string }) {
  const fgRgb = hslTripletToRgb(fg);
  const bgRgb = hslTripletToRgb(bg);
  if (!fgRgb || !bgRgb) {
    return <Badge variant="outline" className="gap-1"><AlertTriangle className="h-3 w-3" /> {label}: invalid</Badge>;
  }
  const ratio = contrastRatio(fgRgb, bgRgb);
  const level = wcagLevel(ratio);
  const ok = level === "AAA" || level === "AA";
  return (
    <Badge variant={ok ? "secondary" : "destructive"} className="gap-1">
      {ok ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {label}: {ratio.toFixed(2)} ({level})
    </Badge>
  );
}

export function ColorsTab() {
  const { getText, isLoading } = useBranding();
  const upsert = useUpsertBranding();

  // Seed local state from server values.
  const serverValues = useMemo(() => {
    const out: Record<ColorKey, string> = {
      primary: "", accent: "", background: "", foreground: "", muted: "", destructive: "",
    };
    for (const { key } of COLOR_KEYS) out[key] = getText(`color.${key}` as const);
    return out;
  }, [getText]);

  const [values, setValues] = useState<Record<ColorKey, string>>(serverValues);

  useEffect(() => {
    setValues(serverValues);
  }, [serverValues]);

  const dirty = useMemo(
    () => COLOR_KEYS.some(({ key }) => values[key] !== serverValues[key]),
    [values, serverValues]
  );

  const applyPalette = (palette: ColorPalette) => {
    setValues(palette.values);
  };

  const save = () => {
    const updates = COLOR_KEYS
      .filter(({ key }) => values[key] !== serverValues[key])
      .map(({ key }) => ({
        key: `color.${key}` as const,
        value_text: values[key],
      }));
    if (updates.length === 0) return;
    upsert.mutate(updates, {
      onSuccess: () => toast.success("Colors saved"),
      onError: (e) => toast.error((e as Error).message || "Save failed"),
    });
  };

  const reset = () => setValues(serverValues);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Curated palettes</CardTitle>
          <CardDescription>
            Click a palette to preview it in the editor below. Nothing is saved
            until you press "Save changes".
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {COLOR_PALETTES.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPalette(p)}
                className="group text-left rounded-lg border border-border/60 bg-card p-3 hover:border-primary/60 hover:bg-muted/40 transition-colors"
              >
                <div className="flex gap-1.5">
                  <Swatch triplet={p.values.primary} className="h-8 flex-1" />
                  <Swatch triplet={p.values.accent} className="h-8 flex-1" />
                  <Swatch triplet={p.values.foreground} className="h-8 flex-1" />
                  <Swatch triplet={p.values.muted} className="h-8 flex-1" />
                </div>
                <p className="mt-3 text-sm font-medium">{p.label}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>Fine-tune colors</CardTitle>
              <CardDescription>
                HSL triplets (e.g. <code className="text-[11px]">129 26% 22%</code>). WCAG contrast
                is checked below — aim for "AA" or better.
              </CardDescription>
            </div>
            {dirty && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={reset} disabled={upsert.isPending}>
                  Reset
                </Button>
                <Button size="sm" onClick={save} disabled={upsert.isPending}>
                  {upsert.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save changes
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {COLOR_KEYS.map(({ key, label, help }) => (
            <div key={key} className="grid gap-2">
              <Label htmlFor={`color-${key}`} className="text-sm font-medium">{label}</Label>
              <div className="flex items-center gap-2">
                <Swatch triplet={values[key]} className="h-9 w-9 shrink-0" />
                <Input
                  id={`color-${key}`}
                  value={values[key]}
                  disabled={isLoading}
                  onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                  placeholder="H S% L%"
                  className="font-mono text-sm"
                />
              </div>
              {help && <p className="text-xs text-muted-foreground">{help}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contrast check</CardTitle>
          <CardDescription>
            WCAG AA requires ≥ 4.5 for body text. ≥ 3 passes for large text only.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <ContrastBadge fg={values.foreground} bg={values.background} label="Text on bg" />
          <ContrastBadge fg={values.background} bg={values.primary} label="Button text" />
          <ContrastBadge fg={values.foreground} bg={values.muted} label="Text on muted" />
          <ContrastBadge fg={values.background} bg={values.destructive} label="Destructive button" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>Approximation of how your colors combine in real UI.</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className="rounded-xl p-6 border"
            style={{
              backgroundColor: `hsl(${values.background})`,
              color: `hsl(${values.foreground})`,
              borderColor: `hsl(${values.muted})`,
            }}
          >
            <h3 className="text-xl font-semibold mb-1">Sample heading</h3>
            <p className="text-sm opacity-80 mb-4">Body text on the background color.</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="inline-flex items-center rounded-md px-4 py-2 text-sm font-medium"
                style={{
                  backgroundColor: `hsl(${values.primary})`,
                  color: `hsl(${values.background})`,
                }}
              >
                Primary action
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded-md px-4 py-2 text-sm font-medium border"
                style={{
                  borderColor: `hsl(${values.accent})`,
                  color: `hsl(${values.accent})`,
                  backgroundColor: "transparent",
                }}
              >
                Accent outline
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded-md px-4 py-2 text-sm font-medium"
                style={{
                  backgroundColor: `hsl(${values.muted})`,
                  color: `hsl(${values.foreground})`,
                }}
              >
                Muted chip
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
